/* eslint-disable no-underscore-dangle */
/** @jsxImportSource @emotion/react */
import React, { useEffect, useRef, useState } from "react";
import styled from "@emotion/styled";
import { useRecoilState } from "recoil";
import { css, useTheme } from "@emotion/react";
import { useLocation } from "wouter";
import Button from "../StyledButton";
import InputSetting from "../InputSetting";
import { useModal } from "../Modal/Modal";
import ChangeImage from "../Modal/container/ChangeImage";
import { BUCKET_URL } from "../../utils/Url";
import readFile from "../../utils/ReadFile";
import { uploadImage } from "../../api/Image";
import lairAtom from "../../atoms/LairAtom";
import { deleteLair, updateLair } from "../../api/Lair";
import { Flex } from "../../utils/HelperStyles";
import StyledSwitch from "../StyledSwitch";
import userInfoAtom from "../../atoms/UserInfoAtom";
import { getUserByUid } from "../../api/User";

const Container = styled.div`
  background-color: ${(props) => props.theme.colors.card.background};
  border-radius: 8px;
  border: 1px solid ${(props) => props.theme.colors.card.border};
  height: min-content;
  display: flex;
  flex-direction: column;
  width: 620px;
  margin-bottom: 30px;
`;

const Title = styled.span`
  font-size: 18px;
  font-weight: 600;
  color: ${(props) => props.theme.colors.text};
  letter-spacing: 0.18px;
  margin: 23px 0px;
  width: 600px;
`;

const Subtitle = styled.span`
  letter-spacing: 0.2px;
  color: ${(props) => props.theme.colors.text};
  font-size: 20px;
  font-weight: 600;
  margin: 25px 0px 0px 38px;
`;

const LairProfile = styled.div`
  display: grid;
  grid-template-areas:
    "image . "
    "image name "
    "image text "
    "image . ";
  grid-template-columns: 72px 200px;
  margin: 30px 75px 15px 67px;
  column-gap: 21px;
`;

const LairImage = styled.img`
  grid-area: image;
  width: 72px;
  height: 72px;
  border-radius: 72px;
`;

const LairName = styled.span`
  grid-area: name;
  font-size: 18px;
  font-weight: 600;
  letter-spacing: 0.18px;
  color: ${(props) => props.theme.colors.text};
  margin-bottom: 3px;
`;

const LairText = styled.span`
  grid-area: text;
  font-size: 16px;
  font-weight: medium;
  letter-spacing: 0.16px;
  color: #0075bb;
  cursor: pointer;
`;

export const Subtext = styled.span`
  font-size: 15px;
  letter-spacing: 0.15px;
  color: #818a91;
  padding: 0px 0px 42px 160px;
`;

const SwitchDiv = styled.div`
  ${Flex};
  flex-direction: row;
  justify-content: flex-start;
  padding: 20px 26px 8px 28px;
`;

const SwitchLabel = styled.span`
  letter-spacing: 0.16px;
  font-size: 16px;
  font-weight: 600;
  text-align: right;
  width: 110px;
  color: ${(props) => props.theme.colors.text};
  margin-right: 22px;
`;

const ModalTitle = styled.span`
  color: ${(props) => props.theme.colors.text};
  font-weight: 600;
  font-size: 18px;
`;

const Delete = styled.span`
  font-size: 16px;
  font-weight: 600;
  letter-spacing: 0.16px;
  color: red;
  margin-left: 65px;
  cursor: pointer;
`;

export default function LairSettings(): JSX.Element | null {
  const [lair, setLair] = useRecoilState<any>(lairAtom);
  const [userInfo, setUserInfo] = useRecoilState<any>(userInfoAtom);
  const modal = useModal();
  const theme = useTheme();
  const [disable, setDisable] = useState(false);
  const [approvePosts, setApprovePosts] = useState(false);
  const [privacy, setPrivacy] = useState(false);
  const imageRef = useRef<HTMLInputElement>(null);
  const lairNameRef = useRef<HTMLInputElement>(null);
  const descriptionRef = useRef<HTMLInputElement>(null);
  const locationRef = useRef<HTMLInputElement>(null);
  const [image, setImage] = useState<Blob | null>(null);
  const [isLoading, setLoading] = useState(false);
  const [, setLocation] = useLocation();

  useEffect(() => {
    if (lair) {
      setApprovePosts(lair.approvePosts);
      setPrivacy(lair.privacy);
    }
  }, [lair]);

  const onFileChange = async (e: any) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      const imageDataUrl: string = await readFile(file);

      modal.pushModal(
        <ChangeImage
          imageSrc={imageDataUrl}
          closeModal={() => modal.popModal()}
          saveImage={(croppedImage: Blob) => {
            setImage(croppedImage);
          }}
        />,
        <ModalTitle>Update Your Lair Photo</ModalTitle>
      );
    }
  };

  function checkInput(event: any) {
    if (event.target.value.length < 1) setDisable(true);
    else if (disable && event.target.value.length > 1) setDisable(false);
  }

  async function sendUpdateLair(imageId: string | null) {
    const success = await updateLair({
      _id: lair._id,
      name: lairNameRef.current?.value,
      description: descriptionRef.current?.value,
      image: imageId || lair.image,
      location: locationRef.current?.value,
      approvePosts,
      privacy,
    });

    if (success) {
      setLair({
        ...lair,
        name: lairNameRef.current?.value,
        description: descriptionRef.current?.value,
        image: imageId || lair.image,
        location: locationRef.current?.value,
        approvePosts,
        privacy,
      });

      const user = await getUserByUid(userInfo.uid);
      setUserInfo(user);
    }

    return false;
  }

  if (!lair || !userInfo) return null;
  return (
    <>
      <Title>Lair Settings</Title>
      <Container>
        <Subtitle>Lair Information</Subtitle>
        <LairProfile>
          <LairImage
            src={
              image
                ? URL.createObjectURL(image)
                : `${BUCKET_URL}/file/${lair.image}`
            }
          />
          <LairName>{lair.name}</LairName>
          <LairText onClick={() => imageRef.current?.click()}>
            <input
              ref={imageRef}
              type="file"
              accept="image/*"
              onChange={onFileChange}
              style={{ display: "none" }}
            />
            Change your lair picture
          </LairText>
        </LairProfile>

        <InputSetting
          ref={lairNameRef}
          onChange={checkInput}
          defaultValue={lair.name}
        >
          Lair name
        </InputSetting>

        <InputSetting
          ref={descriptionRef}
          onChange={checkInput}
          defaultValue={lair.description}
        >
          Description
        </InputSetting>

        <InputSetting
          ref={locationRef}
          onChange={checkInput}
          defaultValue={lair.location}
        >
          Location
        </InputSetting>
        <SwitchDiv>
          <SwitchLabel>Lair Privacy</SwitchLabel>
          <StyledSwitch
            toggled={privacy}
            whenToggled={(res) => {
              setPrivacy(res);
            }}
          />
        </SwitchDiv>

        <Subtext
          css={css`
            border-bottom: 1px solid;
            border-color: ${theme.colors.border};
          `}
        >
          Only members can see the activity and files shared in this lair.
        </Subtext>

        <Subtitle>Manage Lair Activity</Subtitle>

        <InputSetting onChange={checkInput} defaultValue="123">
          Who Can Post
        </InputSetting>

        <SwitchDiv>
          <SwitchLabel>Approve Posts</SwitchLabel>
          <StyledSwitch
            toggled={approvePosts}
            whenToggled={(res) => {
              setApprovePosts(res);
            }}
          />
        </SwitchDiv>
        <Subtext>
          Posts by members must be approved by an admin or moderator.
        </Subtext>

        <Delete
          onClick={async () => {
            const success = await deleteLair(lair._id);
            if (success) {
              // setLair({});
              setLocation("/home");
              const user = await getUserByUid(userInfo.uid);
              if (user) setUserInfo(user);
            }
          }}
        >
          Delete lair
        </Delete>

        <Button
          inverted
          style={{ alignSelf: "flex-end", margin: "0px 43px 20px 0px" }}
          disable={disable}
          isLoading={isLoading}
          onClick={async () => {
            setLoading(true);
            if (image) {
              const response = await uploadImage(image);
              if (response.status === 200) {
                const loading = await sendUpdateLair(response.id);
                setLoading(loading);
              }
            } else {
              const loading = await sendUpdateLair(null);
              setLoading(loading);
            }
          }}
        >
          Save
        </Button>
      </Container>
    </>
  );
}
